import React, { useMemo } from 'react';

import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import { withErrorBoundary } from 'react-error-boundary';

import { changeTariff } from '@entities/tariff/lib';
import { ToastManager } from '@features';
import { hintStorage } from '@features/hints';
import { UiText } from '@shared/ui';
import { MyTeamContext } from '@widgets/MyTeam/model/context';
import { EmployeesList } from '@widgets/MyTeam/ui/EmployeesList/EmployeesList';
import { EmptyTeam } from '@widgets/MyTeam/ui/EmptyTeam/EmptyTeam';
import { LoadingTeamSkeleton } from '@widgets/MyTeam/ui/LoadingTeamSkeleton/LoadingTeamSkeleton';
import { MyTeamFallback } from '@widgets/MyTeam/ui/MyTeamFallback/MyTeamFallback';
import { MyTeamModals } from '@widgets/MyTeam/ui/MyTeamModals';

import cls from './MyTeam.module.css';
import { useMyTeam } from '../../lib/hooks/useMyTeam';

interface MyTeamProps {
    classes?: string;
}

export const MyTeam = ({ classes }: MyTeamProps) => {
    const {
        t,
        countEmployers,
        currentModal,
        onCloseCurrentModal,
        onChangeCurrentModal,
        totalCount,
        isGetTeamLoading,
        isCanAddEmployee,
        isOwner,
        currentClickedId,
        onChangeCurrentClickedId,
    } = useMyTeam();

    const deletingEmployee = useIsMutating({ mutationKey: ['deleteEmployee'], exact: true }) > 0;

    const contextValues = useMemo(
        () => ({
            currentModal,
            currentClickedId,
            onChangeCurrentModal,
            onCloseCurrentModal,
            onChangeCurrentClickedId,
        }),
        [
            currentModal,
            currentClickedId,
            onChangeCurrentModal,
            onChangeCurrentClickedId,
            onCloseCurrentModal,
        ]
    );

    if (isGetTeamLoading) {
        return <LoadingTeamSkeleton />;
    }

    return (
        <MyTeamContext.Provider value={contextValues}>
            {!isGetTeamLoading && !totalCount && <div data-hint-target="my-team-empty" style={{ display: 'none' }} />}
            {!isGetTeamLoading && !!totalCount && (
                <div data-hint-target="my-team-has-employees" style={{ display: 'none' }} />
            )}
            {!totalCount && (
                <EmptyTeam
                    onClick={async () => {
                        if (isCanAddEmployee) {
                            onChangeCurrentModal('addEmployee');
                            return;
                        }
                        await changeTariff(isOwner);
                    }}
                />
            )}
            <section className={classNames(cls.MyTeam, [classes])}>
                <header className={cls.header}>
                    <UiText tag="h2" variant="subHeading" weight="bold">
                        {t('MyTeam.return.section.header.subHeading')}
                    </UiText>
                    <UiText classes={cls.countEmployers} tag="span" variant="mediumPlain">
                        {countEmployers}
                    </UiText>
                </header>
                <EmployeesList deletingEmployee={deletingEmployee} />
                <MyTeamModals
                    currentModal={currentModal}
                    currentClickedId={currentClickedId}
                    onCloseCurrentModal={onCloseCurrentModal}
                    onChangeCurrentClickedId={onChangeCurrentClickedId}
                    onEmployeeAdded={() => hintStorage.markCompleted('invite-colleague')}
                />
                <ToastManager toastPosition="right" containerStyles={cls.ToastStyles} />
            </section>
        </MyTeamContext.Provider>
    );
};

export default withErrorBoundary(MyTeam, {
    FallbackComponent: MyTeamFallback,
});
